<template>
  <Dialog
    :closable="false"
    :header="header_text"
    :modal="true"
    :visible="is_open"
    :baseZIndex="base_z_index"
    :draggable="false"
    class="w-dialog"
    :style="dialog_style"
    @hide="onHide"
  >
    <p
      v-if="message_text"
      v-html="message_text"
    />

    <div
      v-if="is_destructive && object_name"
      class="mt-3"
    >
      <p class="mb-2 text-sm">
        {{ t('modal.labels.typeToConfirm', { confirmationInput: object_name }) }}
      </p>

      <InputText
        ref="input_ref"
        v-model="confirmation_input"
        class="p-inputtext w-full border-danger-600 focus-ring-danger"
        data-test="modal-confirm-type-input"
        :placeholder="t('modal.labels.typeHere')"
        @keydown.enter="onAccept"
      />
    </div>

    <template #footer>
      <Button
        data-test="modal-cancel-primary"
        :label="cancel_label"
        severity="secondary"
        variant="text"
        @click="onCancel"
      />
      <Button
        data-test="modal-confirm-primary"
        :disabled="is_confirm_disabled"
        :label="accept_label"
        :severity="is_destructive ? 'danger' : 'primary'"
        @click="onAccept"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import DOMPurify from 'dompurify';
import { useI18n } from 'vue-i18n';
import useGlobalConfirm from '@/composables/useGlobalConfirm';

const { t } = useI18n();
const {
  is_open,
  is_destructive,
  object_name,
  base_z_index,
  custom_message,
  custom_header,
  custom_accept_label,
  custom_cancel_label,
  custom_max_width,
  handleConfirm,
  handleCancel,
} = useGlobalConfirm();

const MAX_WIDTH_DEFAULT = '460px';

const confirmation_input = ref('');
const input_ref = ref(null);

const header_text = computed(
  () => custom_header.value || (is_destructive.value ? t('modal.labels.dangerZone') : t('modal.labels.areYouSure')),
);

const message_text = computed(() => {
  const message =
    custom_message.value ||
    (is_destructive.value
      ? t('modal.labels.areYouSureDelete', { object: object_name.value || t('modal.labels.this') })
      : '');
  return DOMPurify.sanitize(message);
});

const dialog_style = computed(() => ({ maxWidth: custom_max_width.value || MAX_WIDTH_DEFAULT }));

const cancel_label = computed(() => custom_cancel_label.value || t('modal.actions.cancel'));

const accept_label = computed(() => {
  if (custom_accept_label.value) return custom_accept_label.value;
  return is_destructive.value ? t('modal.actions.remove') : t('modal.actions.submit');
});

const is_confirm_disabled = computed(() => {
  if (!is_destructive.value || !object_name.value) return false;
  return confirmation_input.value.trim().toLowerCase() !== object_name.value.trim().toLowerCase();
});

// Focus input when dialog opens
watch(is_open, async (val) => {
  if (val) {
    confirmation_input.value = '';
    await nextTick();
    try {
      if (input_ref.value && typeof input_ref.value.focus === 'function') {
        input_ref.value.focus();
      } else {
        const el = document.querySelector('.p-dialog .p-inputtext');
        if (el && typeof el.focus === 'function') el.focus();
      }
    } catch {
      /* ignore focus failures */
    }
  } else {
    confirmation_input.value = '';
  }
});

const onAccept = () => {
  if (is_confirm_disabled.value) return;
  confirmation_input.value = '';
  handleConfirm();
};

const onCancel = () => {
  confirmation_input.value = '';
  handleCancel();
};

const onHide = () => {
  confirmation_input.value = '';
  handleCancel();
};
</script>
